import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { customersAPI, analyticsAPI } from '../services/api';
import { useSyncedPreferences } from '../hooks/useSyncedPreferences';
import CustomerTable from '../components/CustomerTable';
import Analytics from '../components/Analytics';
import CSVUpload from '../components/CSVUpload';
import EventsAdmin from '../components/Events/EventsAdmin';
import AnnouncementAdmin from '../components/Announcement/AnnouncementAdmin';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('customers');
  const [allCustomers, setAllCustomers] = useState([]);
  const [highValueCustomers, setHighValueCustomers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copyHighSuccess, setCopyHighSuccess] = useState(''); // '', 'copied', 'error'
  const [copyAllSuccess, setCopyAllSuccess] = useState(''); // '', 'copied', 'error'
  // settings handled on dedicated page
  
  // Sorting state for all customers table
  const [allCustomersSort, setAllCustomersSort] = useState({
    sortBy: 'last_order_date',
    sortOrder: 'desc'
  });
  
  // Use synced preferences for high-value customer filters
  const {
    value: filters,
    setValue: setFilters,
    isLoading: filtersLoading,
    isSyncing: filtersSyncing
  } = useSyncedPreferences('customerFilters', {
    sortBy: 'total_spent',
    sortOrder: 'desc',
    minOrders: '5',
    minSpent: '100',
    daysRange: '90'
  });

  const fetchCustomers = useCallback(async () => {
    console.log('Fetching customers with filters:', filters, 'and allCustomersSort:', allCustomersSort);
    setLoading(true);
    setError('');
    try {
      // Fetch high-value customers with filters
      const highValueParams = {};
      if (filters.sortBy) highValueParams.sortBy = filters.sortBy;
      if (filters.sortOrder) highValueParams.sortOrder = filters.sortOrder;
      if (filters.minOrders) highValueParams.minOrders = filters.minOrders;
      if (filters.minSpent) highValueParams.minSpent = filters.minSpent;
      if (filters.daysRange) highValueParams.daysRange = filters.daysRange;

      console.log('High value params:', highValueParams);
      const highValueResponse = await customersAPI.getAll(highValueParams);
      console.log('High value response:', highValueResponse.data);
      setHighValueCustomers(highValueResponse.data.customers);
      
      // Fetch all customers with separate sorting
      const allParams = {
        sortBy: allCustomersSort.sortBy,
        sortOrder: allCustomersSort.sortOrder
      };
      
      console.log('All customers params:', allParams);
      const allResponse = await customersAPI.getAll(allParams);
      console.log('All customers response:', allResponse.data);
      setAllCustomers(allResponse.data.customers);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError(err.response?.data?.error?.message || 'Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  }, [filters, allCustomersSort]);

  const fetchAnalytics = useCallback(async () => {
    console.log('Fetching analytics...');
    setLoading(true);
    setError('');
    try {
      const response = await analyticsAPI.getOverview();
      console.log('Analytics response:', response.data);
      setAnalytics(response.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.response?.data?.error?.message || 'Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Fetch data regardless of filter sync state to avoid UI blocking
    if (activeTab === 'customers') {
      fetchCustomers();
    } else if (activeTab === 'analytics') {
      fetchAnalytics();
    }
  }, [activeTab, fetchCustomers, fetchAnalytics]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // Note: fetchCustomers and fetchAnalytics are intentionally in deps
  // They update when filters or allCustomersSort change

  const handleUploadSuccess = () => {
    fetchCustomers();
    if (activeTab === 'analytics') {
      fetchAnalytics();
    }
  };

  const handleDeleteCustomer = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer and all their orders?')) {
      return;
    }

    try {
      await customersAPI.delete(id);
      fetchCustomers();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to delete customer');
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const newFilters = {
      ...filters,
      [name]: value
    };
    // This now syncs to backend automatically via useSyncedPreferences hook
    setFilters(newFilters);
  };
  
  const handleAllCustomersSortChange = (e) => {
    const { name, value } = e.target;
    setAllCustomersSort(prev => ({
      ...prev,
      [name]: value
    }));
  };


  
  // Copy high-value customer emails as a comma-separated list to clipboard
  const copyHighValueEmails = async () => {
    if (!highValueCustomers || highValueCustomers.length === 0) {
      setCopyHighSuccess('error');
      setTimeout(() => setCopyHighSuccess(''), 3000);
      return;
    }

    const emailPattern = /^\S+@\S+\.\S+$/;
    const emails = highValueCustomers
      .map(c => (c.email || '').trim())
      .filter(e => e && emailPattern.test(e));

    if (emails.length === 0) {
      setCopyHighSuccess('error');
      setTimeout(() => setCopyHighSuccess(''), 3000);
      return;
    }

    const text = emails.join(', ');
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopyHighSuccess('copied');
      setTimeout(() => setCopyHighSuccess(''), 3000);
    } catch (err) {
      console.error('Copy failed', err);
      setCopyHighSuccess('error');
      setTimeout(() => setCopyHighSuccess(''), 3000);
    }
  };

  const copyAllCustomersEmails = async () => {
    if (!allCustomers || allCustomers.length === 0) {
      setCopyAllSuccess('error');
      setTimeout(() => setCopyAllSuccess(''), 3000);
      return;
    }

    const emailPattern = /^\S+@\S+\.\S+$/;
    const emails = allCustomers
      .map(c => (c.email || '').trim())
      .filter(e => e && emailPattern.test(e));

    if (emails.length === 0) {
      setCopyAllSuccess('error');
      setTimeout(() => setCopyAllSuccess(''), 3000);
      return;
    }

    const text = emails.join(', ');
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopyAllSuccess('copied');
      setTimeout(() => setCopyAllSuccess(''), 3000);
    } catch (err) {
      console.error('Copy failed', err);
      setCopyAllSuccess('error');
      setTimeout(() => setCopyAllSuccess(''), 3000);
    }
  };

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <h1>Royal China Admin</h1>
          <div className="header-actions">
            <span className="user-info">Welcome, {user?.username}</span>
            {filtersSyncing && (
              <span className="sync-indicator" title="Syncing preferences to cloud...">
                🔄 Syncing...
              </span>
            )}
            <button onClick={() => window.location.href = '/admin/settings'} className="btn btn-secondary">Settings</button>
            <button onClick={logout} className="btn btn-secondary">Logout</button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="dashboard-tabs">
        <button
          className={`tab ${activeTab === 'customers' ? 'active' : ''}`}
          onClick={() => setActiveTab('customers')}
        >
          Customers
        </button>
        <button
          className={`tab ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          Analytics
        </button>
        <button
          className={`tab ${activeTab === 'upload' ? 'active' : ''}`}
          onClick={() => setActiveTab('upload')}
        >
          Upload CSV
        </button>
        <button
          className={`tab ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveTab('events')}
        >
          Events
        </button>
        <button
          className={`tab ${activeTab === 'announcement' ? 'active' : ''}`}
          onClick={() => setActiveTab('announcement')}
        >
          Announcement
        </button>
      </div>

      {/* Main Content */}
      <main className="dashboard-content">

        {activeTab === 'customers' && (
          <div className="customers-section">

 {/* Copy Emails Card */}
            <div className="card copy-emails-card">
              <h3>✉️ Copy Emails</h3>
              <div className="card-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={copyHighValueEmails}
                >
                  {copyHighSuccess === 'copied' ? 'Copied High-Value!' : 'Copy High-Value Emails'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={copyAllCustomersEmails}
                >
                  {copyAllSuccess === 'copied' ? 'Copied All!' : 'Copy All Customers Emails'}
                </button>
                {(copyHighSuccess === 'error' || copyAllSuccess === 'error') && (
                  <span className="copy-feedback error">No emails to copy</span>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="section-divider"></div>

            {/* High-Value Customers Section */}
            <div className="card filters-card">
              <h3>🌟 High-Value Customers</h3>
              <div className="filter-info">
                <p className="info-text">
                  💡 <strong>Filter Thresholds:</strong> Customize criteria to identify your best customers. 
                  Settings are synced to your account and available on all devices.
                  {filtersSyncing && <span className="syncing-text"> 🔄 Syncing to cloud...</span>}
                </p>
              </div>
              <div className="filters-grid">
                <div className="filter-group">
                  <label>Sort By</label>
                  <select
                    name="sortBy"
                    value={filters.sortBy}
                    onChange={handleFilterChange}
                    className="input-field"
                  >
                    <option value="last_order_date">Latest Order</option>
                    <option value="name">Name</option>
                    <option value="total_spent">Total Spent</option>
                    <option value="total_orders">Order Count</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label>Order</label>
                  <select
                    name="sortOrder"
                    value={filters.sortOrder}
                    onChange={handleFilterChange}
                    className="input-field"
                  >
                    <option value="desc">Descending</option>
                    <option value="asc">Ascending</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label>
                    Min Orders 
                    <span className="filter-hint">
                      {filters.minOrders ? ` (≥ ${filters.minOrders})` : ' (all)'}
                    </span>
                  </label>
                  <input
                    type="number"
                    name="minOrders"
                    value={filters.minOrders}
                    onChange={handleFilterChange}
                    className="input-field"
                    placeholder="e.g., 5"
                    min="0"
                    step="1"
                  />
                  <small className="field-description">Minimum orders to qualify</small>
                </div>

                <div className="filter-group">
                  <label>
                    Min Spent (£)
                    <span className="filter-hint">
                      {filters.minSpent ? ` (≥ £${filters.minSpent})` : ' (all)'}
                    </span>
                  </label>
                  <input
                    type="number"
                    name="minSpent"
                    value={filters.minSpent}
                    onChange={handleFilterChange}
                    className="input-field"
                    placeholder="e.g., 100"
                    min="0"
                    step="0.01"
                  />
                  <small className="field-description">Minimum lifetime spending</small>
                </div>

                <div className="filter-group">
                  <label>
                    Active Within Days
                    <span className="filter-hint">
                      {filters.daysRange ? ` (${filters.daysRange} days)` : ' (all time)'}
                    </span>
                  </label>
                  <input
                    type="number"
                    name="daysRange"
                    value={filters.daysRange}
                    onChange={handleFilterChange}
                    className="input-field"
                    placeholder="e.g., 90"
                    min="0"
                    step="1"
                  />
                  <small className="field-description">Recent activity period</small>
                </div>

                <div className="filter-group filter-actions">
                  <small className="saved-indicator">
                    {filtersSyncing ? (
                      '🔄 Syncing...'
                    ) : (filters.minOrders || filters.minSpent || filters.daysRange) ? (
                      '✓ Synced across devices'
                    ) : (
                      'Using default filters'
                    )}
                  </small>
                </div>
              </div>
              {/* High-Value Customers Results & Table inside the same card */}
              <div className="card-subsection-divider"></div>
              <div className="card-subsection">
              {!loading && !filtersLoading && highValueCustomers.length > 0 && (
                <div className="results-summary within-card">
                  <p>
                    🌟 <strong>{highValueCustomers.length}</strong> high-value customer{highValueCustomers.length !== 1 ? 's' : ''} found
                    {filters.minOrders && ` with ≥${filters.minOrders} orders`}
                    {filters.minSpent && ` who spent ≥£${filters.minSpent}`}
                    {filters.daysRange && ` active in last ${filters.daysRange} days`}
                  </p>
                </div>
              )}

              {loading ? (
                <div className="loading">Loading high-value customers...</div>
              ) : highValueCustomers.length > 0 ? (
                <CustomerTable
                  customers={highValueCustomers}
                  onDelete={handleDeleteCustomer}
                />
              ) : (
                <div className="empty-state within-card">
                  <p>No customers match the high-value criteria. Try adjusting the filters above.</p>
                </div>
              )}
              </div>
            </div>

            {/* Divider */}
            <div className="section-divider"></div>

            {/* All Customers Section */}
            <div className="card all-customers-card">
              <h3>📋 All Customers</h3>
              <div className="all-customers-controls">
                <div className="filter-group">
                  <label>Sort By</label>
                  <select
                    name="sortBy"
                    value={allCustomersSort.sortBy}
                    onChange={handleAllCustomersSortChange}
                    className="input-field"
                  >
                    <option value="last_order_date">Latest Order</option>
                    <option value="name">Name</option>
                    <option value="total_spent">Total Spent</option>
                    <option value="total_orders">Order Count</option>
                    <option value="created_at">Customer Since</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label>Order</label>
                  <select
                    name="sortOrder"
                    value={allCustomersSort.sortOrder}
                    onChange={handleAllCustomersSortChange}
                    className="input-field"
                  >
                    <option value="desc">Descending</option>
                    <option value="asc">Ascending</option>
                  </select>
                </div>
              </div>
            </div>

            {/* All Customers Results */}
            {!loading && allCustomers.length > 0 && (
              <div className="results-summary">
                <p>
                  Showing <strong>{allCustomers.length}</strong> total customer{allCustomers.length !== 1 ? 's' : ''}
                </p>
              </div>
            )}

            {/* All Customers Table */}
            {loading ? (
              <div className="loading">Loading all customers...</div>
            ) : (
              <CustomerTable
                customers={allCustomers}
                onDelete={handleDeleteCustomer}
              />
            )}
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="analytics-section">
            {loading ? (
              <div className="loading">Loading analytics...</div>
            ) : analytics ? (
              <Analytics data={analytics} />
            ) : null}
          </div>
        )}

        {activeTab === 'upload' && (
          <div className="upload-section">
            <CSVUpload onSuccess={handleUploadSuccess} />
          </div>
        )}

        {activeTab === 'events' && (
          <div className="events-section">
            <EventsAdmin />
          </div>
        )}

        {activeTab === 'announcement' && (
          <div className="announcement-section">
            <AnnouncementAdmin />
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
